import type React from 'react';

import { cn } from '@elara/client-core/lib/utils';

const EDGE_PADDING_CLASSES = 'px-4 md:px-8';

/**
 * The full-bleed cover header of the full-page itinerary: trip title and an optional subtitle (dates,
 * and costs on the owner's page) centred over the destination image. Purely presentational — callers
 * pass any actions (e.g. the owner's "Book trip" button) as `actions`, shown in a row under the
 * subtitle, and a `cornerAction` (e.g. the owner's share icon button) pinned to the top right.
 */
export function ItineraryHero({
  title,
  subtitle,
  coverImage,
  actions,
  cornerAction,
}: {
  title: string;
  subtitle?: React.ReactNode;
  coverImage?: string;
  actions?: React.ReactNode;
  cornerAction?: React.ReactNode;
}) {
  return (
    <div
      data-print="hero"
      className="relative w-full md:h-[90dvh] bg-background-tertiary"
      style={{
        backgroundImage: coverImage ? `url(${coverImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Controls are screen-only: neither action slot prints. */}
      {cornerAction && (
        <div className="absolute top-4 right-4 md:top-6 md:right-8 z-10 print:hidden">{cornerAction}</div>
      )}

      <div
        data-print="hero-overlay"
        className={cn(
          'w-full h-full flex flex-col justify-center items-center',
          coverImage ? 'bg-gradient-to-b from-camel-950/40 to-camel-950/80' : 'py-8',
        )}
      >
        <div
          className={cn(
            'flex flex-col items-center gap-4 md:gap-6 max-w-[1400px] py-24 md:py-0 mx-auto text-center',
            EDGE_PADDING_CLASSES,
            coverImage && 'text-white',
          )}
        >
          <h2 className="text-3xl md:text-6xl font-display font-light leading-tight">{title}</h2>
          {subtitle && (
            <div
              data-print="hero-subtitle"
              className={cn(
                'font-display font-light text-lg md:text-xl',
                coverImage ? 'text-white/85' : 'text-muted-foreground',
              )}
            >
              {subtitle}
            </div>
          )}
          {actions && <div className="flex flex-wrap justify-center gap-3 mt-2 print:hidden">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
